import React, { useState, useEffect, useCallback, memo } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  Button, 
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Pagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  PlusCircle, 
  MessageSquare, 
  Search, 
  Trash2, 
  Edit2, 
  X,
  Calendar,
  Clock,
  RefreshCw
} from 'lucide-react';
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import useChatWebSocket, { sendMessage } from '../websocket';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../../../i18n';

const ChatHistoryPanel = memo(({ 
  darkMode, 
  onSelectChat, 
  onCreateNewChat, 
  currentChatId,
  isLoading,
  websocket,
  connectionError,
  onReconnect
}) => {
  const { t } = useTranslation('assistant');
  const [chatHistory, setChatHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatName, setEditingChatName] = useState('');
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Add state for confirmation dialog
  const [openDialog, setOpenDialog] = useState(false);

  // Load chat history from WebSocket
  useEffect(() => {
    const fetchChatHistory = () => {
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        setHistoryLoading(true);
        try {
          websocket.send(JSON.stringify({
            type: 'fetch_user_chats',
            page: page,
            page_size: pageSize
          }));
        } catch (error) {
          console.error('Error fetching chat history:', error);
          setError(t('errors.failedLoadChatHistory'));
          setHistoryLoading(false);
        }
      } else {
        // WebSocket not available or not connected
        //console.log('WebSocket not connected, using mock data for chat history');
        setError(t('errors.websocketUnavailable'));
        
        // Provide mock data if WebSocket is not connected (for development)
        if (process.env.NODE_ENV === 'development') {
          const mockHistory = [
            { id: '1', title: 'Chat with client Ferrari', lastMessage: 'The Ferrari 488 is available', date: new Date().toISOString(), messages: 12 },
            { id: '2', title: 'Lamborghini inquiry', lastMessage: 'We have 3 models available', date: new Date(Date.now() - 86400000).toISOString(), messages: 8 },
            { id: '3', title: 'Customer support', lastMessage: 'Thank you for your assistance', date: new Date(Date.now() - 172800000).toISOString(), messages: 15 },
          ];
          setChatHistory(mockHistory.map(chat => ({
            ...chat,
            rawData: { is_connected: chat.id === '1' }
          })));
          setTotalPages(1);
          setTotalCount(3);
        }
        
        setHistoryLoading(false);
      }
    };

    fetchChatHistory();

    // Set up event listener for chat history response
    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'user_chats') {
          setChatHistory(data.chats.map(chat => ({
            id: chat.chat_id,
            title: getChatTitle(chat),
            lastMessage: getLastMessagePreview(chat),
            date: chat.last_message?.created_at || chat.last_connected || chat.created_at,
            messages: countMessagesEstimate(chat),
            rawData: chat // Keep the raw data for reference
          })));
          setTotalPages(data.total_pages);
          setTotalCount(data.total_count);
          setHistoryLoading(false);
          setError(null);
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
        setError(t('errors.failedProcessServerResponse'));
        setHistoryLoading(false);
      }
    };

    if (websocket) {
      websocket.addEventListener('message', handleMessage);
    }

    return () => {
      if (websocket) {
        websocket.removeEventListener('message', handleMessage);
      }
    };
  }, [websocket, page, pageSize]);

  // Helper functions to format chat data
  const getChatTitle = (chat) => {
    // Extract a reasonable title from the first few messages
    // If there's no message data, use a timestamp-based title
    if (chat.title) return chat.title;
    
    const dateStr = new Date(chat.created_at).toLocaleDateString();
    if (chat.last_message?.sender_message) {
      // Use first few words of the first message as title
      const words = chat.last_message.sender_message.split(' ').slice(0, 5).join(' ');
      return words.length < chat.last_message.sender_message.length ? 
        `${words}... (${dateStr})` : `${words} (${dateStr})`;
    }
    
    return t('chatHistory.fallbackTitle', { defaultValue: 'Chat from {{date}}', date: dateStr });
  };

  const getLastMessagePreview = (chat) => {
    if (chat.last_message) {
      // Prefer bot message for the preview, fallback to user message
      const message = chat.last_message.bot_message || chat.last_message.sender_message;
      if (message) {
        return message.length > 60 ? message.substring(0, 60) + '...' : message;
      }
    }
    return t('chatHistory.empty.noMessages');
  };

  const countMessagesEstimate = (chat) => {
    // Backend might provide a message count in the future
    // For now, we'll just return a placeholder if we have messages
    return chat.last_message ? 1 : 0;
  };

  // Filter chats based on search term
  const filteredChats = chatHistory.filter(chat => 
    chat.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Format relative time from date
  const formatRelativeTime = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return t('time.today');
    } else if (diffDays === 1) {
      return t('time.yesterday');
    } else if (diffDays < 7) {
      return t('time.daysAgo', { count: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  }, [t]);

  // Optimize handlers with useCallback
  const startEditing = useCallback((chat) => {
    setEditingChatId(chat.id);
    setEditingChatName(chat.title);
  }, []);

  const saveEditedChatName = useCallback(() => {
    if (editingChatName.trim() && editingChatId && websocket) {
      try {
        websocket.send(JSON.stringify({
          type: 'update_chat',
          chat_id: editingChatId,
          title: editingChatName.trim()
        }));
        
        // Optimistically update local state
        setChatHistory(prevHistory => 
          prevHistory.map(chat => 
            chat.id === editingChatId ? { ...chat, title: editingChatName.trim() } : chat
          )
        );
      } catch (error) {
        console.error('Error updating chat title:', error);
      }
    }
    setEditingChatId(null);
  }, [editingChatName, editingChatId, websocket]);

  // Handle deleting a chat
  const deleteChat = useCallback((chatId) => {
    if (websocket) {
      try {
        websocket.send(JSON.stringify({
          type: 'delete_chat',
          chat_id: chatId
        }));
        
        // Optimistically update UI
        setChatHistory(prevHistory => prevHistory.filter(chat => chat.id !== chatId));
        
        // If the current chat is deleted, create a new one
        if (chatId === currentChatId) {
          onCreateNewChat();
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  }, [websocket, currentChatId, onCreateNewChat]);

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  // Handle manual refresh
  const handleRefresh = useCallback(() => {
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      setHistoryLoading(true);
      websocket.send(JSON.stringify({
        type: 'fetch_user_chats',
        page: page,
        page_size: pageSize
      }));
    } else {
      setError(t('errors.websocketUnavailableRetry'));
      // Auto-retry connection after a delay
      setTimeout(() => {
        if (websocket && websocket.readyState === WebSocket.OPEN) {
          handleRefresh();
        }
      }, 2000);
    }
  }, [websocket, page, pageSize]);

  // Add handlers for dialog
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  // Add handler for clear history
  const handleClearHistory = () => {
    setOpenDialog(false);
    if (websocket) {
      // Send clear_chat_history message to backend
      const success = sendMessage(websocket, { type: 'clear_chat_history' });
      if (success) {
        // Only update UI if message was sent successfully
        setChatHistory([]);
        // Show toast notification
        toast.success(t('chatHistory.messages.historyCleared'));
      } else {
        toast.error(t('chatHistory.messages.clearFailed'));
      }
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      borderRight: 1,
      borderColor: 'divider',
      bgcolor: darkMode ? '#1A1A1A' : '#F5F5F5',
      width: '280px'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('chatHistory.title')}
          </Typography>
          <Box display="flex" alignItems="center">
            {/* Wrap disabled button in span to make Tooltip work */}
            <Tooltip title={t('chatHistory.tooltips.refresh')}>
              <span>
                <IconButton
                  onClick={handleRefresh}
                  disabled={isLoading}
                  sx={{ 
                    color: 'primary.main',
                    '&.Mui-disabled': {
                      color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            {/* Wrap disabled button in span to make Tooltip work */}
            <Tooltip title={t('chatHistory.tooltips.clear')}>
              <span>
                <IconButton
                  onClick={handleOpenDialog}
                  disabled={isLoading || chatHistory.length === 0}
                  sx={{ 
                    color: 'error.main',
                    '&.Mui-disabled': {
                      color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <ClearAllIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        
        <Button 
          variant="contained" 
          startIcon={<PlusCircle size={18} />}
          fullWidth
          onClick={onCreateNewChat}
          sx={{ 
            borderRadius: '8px',
            py: 1,
            textTransform: 'none',
            fontWeight: 'medium'
          }}
        >
          {t('chatHistory.buttons.newChat')}
        </Button>
        
        <TextField
          placeholder={t('chatHistory.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton 
                  size="small" 
                  onClick={() => setSearchTerm('')}
                  edge="end"
                >
                  <X size={16} />
                </IconButton>
              </InputAdornment>
            ),
            sx: { 
              borderRadius: '8px',
              bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'
              }
            }
          }}
        />
      </Box>
      
      {/* Chat List */}
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        px: 1,
        pt: 1
      }}>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (error || connectionError) ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error" sx={{ fontSize: '0.9rem', mb: 2 }}>
              {error || connectionError || t('chatHistory.connectionError')}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={connectionError ? onReconnect : handleRefresh}
              startIcon={<RefreshCw size={16} />}
            >
              {connectionError ? t('chatHistory.buttons.reconnect') : t('chatHistory.buttons.retry')}
            </Button>
          </Box>
        ) : (
          <>
            {filteredChats.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  {searchTerm ? t('chatHistory.empty.noMatch') : t('chatHistory.empty.noHistory')}
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filteredChats.map((chat) => (
                  <React.Fragment key={chat.id}>
                    <ListItem 
                      disablePadding
                      sx={{ 
                        borderRadius: '8px',
                        mb: 0.5,
                        bgcolor: currentChatId === chat.id 
                          ? (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)')
                          : 'transparent',
                        '&:hover': {
                          bgcolor: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'
                        },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <Box
                        onClick={() => {
                          if (editingChatId !== chat.id) {
                            //console.log(`Selecting chat from history: ${chat.id}`);
                            onSelectChat(chat.id);
                          }
                        }}
                        sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                          p: 1.5,
                          cursor: 'pointer',
                          width: '100%'
                        }}
                      >
                        {editingChatId === chat.id ? (
                          <Box sx={{ display: 'flex', width: '100%', mb: 0.5 }}>
                            <TextField
                              value={editingChatName}
                              onChange={(e) => setEditingChatName(e.target.value)}
                              autoFocus
                              size="small"
                              fullWidth
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEditedChatName();
                                if (e.key === 'Escape') setEditingChatId(null);
                              }}
                              InputProps={{
                                sx: { fontSize: '0.9rem' }
                              }}
                            />
                            <IconButton size="small" onClick={saveEditedChatName}>
                              <Tooltip title={t('chatHistory.tooltips.save')}>
                                <Edit2 size={16} />
                              </Tooltip>
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 0.5
                          }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ 
                                fontWeight: currentChatId === chat.id ? 'bold' : 'medium',
                                fontSize: '0.95rem',
                                maxWidth: '170px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {chat.title}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title={t('chatHistory.tooltips.rename')}>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditing(chat);
                                  }}
                                  sx={{ 
                                    p: 0.5,
                                    '&:hover': {
                                      bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
                                    }
                                  }}
                                >
                                  <Edit2 size={14} />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title={t('chatHistory.tooltips.delete')}>
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteChat(chat.id);
                                  }}
                                  sx={{ 
                                    p: 0.5,
                                    '&:hover': {
                                      bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                      color: 'error.main'
                                    }
                                  }}
                                >
                                  <Trash2 size={14} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.8rem',
                              maxWidth: '210px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {chat.lastMessage}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mt: 0.5
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Clock size={12} color={darkMode ? "#aaa" : "#666"} />
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {formatRelativeTime(chat.date)}
                            </Typography>
                          </Box>
                          
                          {chat.messages > 0 && (
                            <Tooltip title={t('chatHistory.messagesCount', { defaultValue: '{{count}} messages', count: chat.messages })}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                borderRadius: '10px',
                                px: 0.8,
                                py: 0.2
                              }}>
                                <MessageSquare size={12} color={darkMode ? "#aaa" : "#666"} />
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary"
                                  sx={{ ml: 0.5, fontSize: '0.7rem' }}
                                >
                                  {chat.messages}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                          {chat.rawData?.is_connected && (
                            <Box sx={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              bgcolor: 'success.main',
                              boxShadow: '0 0 0 2px rgba(0,200,83,0.2)'
                            }} />
                          )}
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider sx={{ opacity: 0.5 }} />
                  </React.Fragment>
                ))}
              </List>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 2 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: darkMode ? 'rgba(255,255,255,0.7)' : undefined,
                      '&.Mui-selected': {
                        bgcolor: darkMode ? 'rgba(255,255,255,0.2)' : undefined
                      }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Add Dialog for confirmation */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('chatHistory.dialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('chatHistory.dialog.description')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            {t('chatHistory.dialog.cancel')}
          </Button>
          <Button onClick={handleClearHistory} color="error" autoFocus>
            {t('chatHistory.dialog.clear')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ChatHistoryPanel; 
